import NiceModal from '@ebay/nice-modal-react';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import SearchInput from 'components/field/SearchInput';
import Status from 'components/field/Status';
import Card from 'components/global/Card';
import ConfirmationModal from 'components/global/ConfirmationModal';
import PageHeader from 'components/global/PageHeader';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useModalRouter from 'hooks/useModalRouter';

import axios from 'services/axios';
import { ADMIN } from 'services/consts';

import { setLoading } from 'stores/layoutSlice';

import AppLogger from 'utils/AppLogger';

import { faAdd, faPenToSquare, faPlus, faTrashCan } from '@fortawesome/pro-solid-svg-icons';

/**
 * @author Burak Unsal
 */
const RoleTree = () => {
	const { showModal } = useModalRouter();
	const { showSuccess, showError } = useAlert();
	const dispatch = useDispatch();

	const columns = useMemo(
		() => [
			{
				header: 'name',
				accessorKey: 'langResource',
				showOnMobile: true
			},
			{
				header: 'role',
				accessorKey: 'path',
				showOnMobile: true
			},
			{
				header: 'type',
				renderCell: ({ row }) => <Status value={row.original.type} />,
				size: 50,
				showOnMobile: true
			}
		],
		[]
	);

	const [data, setData] = useState([]);

	const convertDataToTreeData = useCallback((arr) => {
		const { res } = arr.reduce(
			(acc, curr) => {
				if (acc.parentMap[curr.parentID]) {
					if (!acc.parentMap[curr.parentID].children) {
						acc.parentMap[curr.parentID].children = [];
					}
					acc.parentMap[curr.parentID].children.push(curr);
				} else {
					acc.res.push(curr);
				}
				acc.parentMap[curr.ID] = curr;
				return acc;
			},
			{ parentMap: {}, res: [] }
		);

		return res;
	}, []);

	const fetchData = useCallback(async () => {
		dispatch(setLoading(true));
		await axios
			.post(ADMIN.ROLE_TREE.GET_ROLE_TREE_LIST, {
				paging: { offset: 0, count: 1000 },
				totalRecord: false
			})
			.then((response) => {
				const { data, status } = response;

				if (status !== 200) {
					AppLogger.error(response);
					showError();
				} else {
					setData(convertDataToTreeData(data.data));
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
		dispatch(setLoading(false));
	}, []);

	useEffect(() => {
		fetchData();
	}, []);

	useEffect(() => {
		window.addEventListener('updateRoleTree', fetchData);

		return () => {
			window.removeEventListener('updateRoleTree', fetchData);
		};
	}, []);

	const [searchValue, setSearchValue] = useState(null);

	const filter = useCallback((arr, key) => {
		const getNodes = (result, object) => {
			if (
				object.langResource.toLowerCase().includes(key.toLowerCase()) ||
				object.path.toLowerCase().includes(key.toLowerCase())
			) {
				result.push(object);
				return result;
			}
			if (Array.isArray(object.children)) {
				const nodes = object.children.reduce(getNodes, []);
				if (nodes.length) result.push({ ...object, children: nodes });
			}
			return result;
		};

		return arr.reduce(getNodes, []);
	}, []);

	const filteredData = useMemo(() => {
		if (searchValue) {
			return filter(data, searchValue);
		} else {
			return data;
		}
	}, [searchValue, data]);

	const contextMenuItems = useMemo(
		() => [
			{
				icon: faAdd,
				label: 'add',
				action: (row) => {
					showModal('role-tree', {
						id: row.original.ID,
						mode: 'add'
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'edit',
				action: (row) => {
					showModal('role-tree', {
						id: row.original.ID,
						mode: 'edit'
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'delete',
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'Delete Role Tree Record',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axios
								.post(ADMIN.ROLE_TREE.DELETE_ROLE_TREE_RECORD, {
									ID: row.original.ID
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatchEvent(new Event('updateRoleTree'));
								});

							dispatch(setLoading(false));
						}
					});
				}
			}
		],
		[]
	);

	return (
		<div>
			<PageHeader title="roleTree">
				<Button icon={faPlus} label="add" onClick={() => showModal('role-tree')} />
			</PageHeader>

			<Card
				style={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					gap: '2rem'
				}}>
				<SearchInput fullWidth value={searchValue} setValue={setSearchValue} />
				<Table
					columns={columns}
					data={filteredData}
					isTree={true}
					enableRowSelection={true}
					hideRowSelectionColumn={true}
					contextMenuItems={contextMenuItems}
				/>
			</Card>
		</div>
	);
};

export default RoleTree;
